import * as FC from "@fluentui/react-components";
import { useEffect, useMemo, useState } from "react";
import {
    equipmentTypeLabel,
    equipmentTypeOptions,
    getEquipmentOptions,
} from "../../services/equipment_service";

const useStyles = FC.makeStyles({
    root: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "end",
        ...FC.shorthands.gap("8px"),
    },
    field: {
        display: "flex",
        minWidth: "220px",
        flexDirection: "column",
        ...FC.shorthands.gap("4px"),
    },
    option: {
        display: "flex",
        alignItems: "center",
        ...FC.shorthands.gap("6px"),
    },
});

const optionLabel = (option) => {
    if (!option) {
        return "";
    }
    const parts = [option.assetCode, option.name].filter(Boolean);
    if (option.model) {
        parts.push(option.model);
    }
    return parts.join(" · ");
};

/**
 * Reusable picker over GET /equipment/options (未归档且可用的器材轻量列表).
 * The options endpoint takes no type parameter, so the type narrowing happens over the
 * already-fetched lightweight list; this is a reference picker, not the paged ledger.
 *
 * @param {object} props
 * @param {number} [props.tenantId] Admin only; other roles are scoped by the backend.
 * @param {() => Promise<object[]>} [props.loadOptions] Overrides the default source. The session
 *   kit editor passes a session-scoped loader: the ledger's list offers only `available` units,
 *   which hides both the units a job already holds and the ones another job holds — and the latter
 *   are exactly what the re-assignment confirmation is for.
 * @param {string} [props.type] fixed type filter; when omitted the user picks the type.
 * @param {number|string} [props.value] selected equipment id
 * @param {(id: number|null, option: object|null) => void} props.onChange
 */
export const EquipmentSelector = ({
    tenantId,
    loadOptions,
    type,
    value,
    onChange,
    label = "器材",
    disabled = false,
}) => {
    const styles = useStyles();
    const [options, setOptions] = useState([]);
    const [typeFilter, setTypeFilter] = useState(type || "");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setTypeFilter(type || "");
    }, [type]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError("");
        const load = loadOptions ? loadOptions() : getEquipmentOptions(tenantId);
        load
            .then((items) => {
                if (!cancelled) {
                    setOptions(items);
                }
            })
            .catch((loadError) => {
                console.error("Failed to load equipment options:", loadError);
                if (!cancelled) {
                    setOptions([]);
                    setError(loadError?.message || "加载可选器材失败");
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [tenantId, loadOptions]);

    const visibleOptions = useMemo(
        () => (typeFilter ? options.filter((item) => item.type === typeFilter) : options),
        [options, typeFilter]
    );

    const selected = options.find((item) => String(item.id) === String(value)) || null;

    return (
        <div className={styles.root}>
            {!type && (
                <div className={styles.field} style={{ minWidth: 140 }}>
                    <FC.Label>器材类型</FC.Label>
                    <FC.Dropdown
                        disabled={disabled}
                        selectedOptions={typeFilter ? [typeFilter] : [""]}
                        value={typeFilter ? equipmentTypeLabel(typeFilter) : "全部类型"}
                        onOptionSelect={(_, data) => setTypeFilter(data.optionValue || "")}
                    >
                        <FC.Option value="">全部类型</FC.Option>
                        {equipmentTypeOptions.map((option) => (
                            <FC.Option key={option.value} value={option.value}>
                                {option.label}
                            </FC.Option>
                        ))}
                    </FC.Dropdown>
                </div>
            )}
            <div className={styles.field}>
                <FC.Label>{label}</FC.Label>
                <FC.Dropdown
                    disabled={disabled || loading}
                    selectedOptions={value ? [String(value)] : []}
                    value={optionLabel(selected)}
                    placeholder={loading ? "加载中..." : "选择器材"}
                    onOptionSelect={(_, data) => {
                        const picked =
                            options.find((item) => String(item.id) === data.optionValue) || null;
                        onChange?.(picked ? picked.id : null, picked);
                    }}
                >
                    {visibleOptions.map((option) => (
                        <FC.Option
                            key={option.id}
                            value={String(option.id)}
                            text={optionLabel(option)}
                            // Server-computed; absent on the ledger's own list, where everything on
                            // offer is selectable by construction.
                            disabled={option.canSelect === false}
                        >
                            <span className={styles.option}>
                                <span>{optionLabel(option)}</span>
                                {option.maintenanceState === "overdue" && (
                                    <FC.Badge appearance="tint" color="danger" size="small">
                                        保养逾期
                                    </FC.Badge>
                                )}
                                {/* Design 8.6 asks for the warranty warning alongside the
                                    maintenance one; an expired warranty changes who pays for a
                                    failure in the field. */}
                                {option.warrantyState === "expired" && (
                                    <FC.Badge appearance="tint" color="warning" size="small">
                                        保修过期
                                    </FC.Badge>
                                )}
                                {option.heldBySessionSeq && (
                                    <FC.Badge appearance="tint" color="informative" size="small">
                                        {`占用中 · ${option.heldBySessionSeq}`}
                                    </FC.Badge>
                                )}
                                {option.isOnThisSession && (
                                    <FC.Badge appearance="tint" color="success" size="small">
                                        本次已选
                                    </FC.Badge>
                                )}
                            </span>
                        </FC.Option>
                    ))}
                </FC.Dropdown>
                {error && <FC.Caption1 style={{ color: FC.tokens.colorPaletteRedForeground1 }}>{error}</FC.Caption1>}
            </div>
        </div>
    );
};
